import { useRpQuery } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type { CreateContractInput } from '~/resources/graphql/graphql'
import { graphql } from '~/resources/graphql'
import { useActiveFacility } from '~/composables/use-active-facility'
import { useShowAlert } from '~/composables/use-show-alert'
import { useSetCartData } from '~/composables/use-cart'
import { useViewer } from '#imports'
import { useSelectedLanguage } from '~/composables/use-selected-language'

export function useRecurringProgramCreateContractMutation() {
    const query = useRpQuery()
    const selectedLanguage = useSelectedLanguage()
    const activeFacility = useActiveFacility()
    const showAlert = useShowAlert()
    const setCartData = useSetCartData()
    const viewer = useViewer()

    return useMutation({
        mutationFn: async (mutationData: CreateContractInput) => {
            const response = await query(
                graphql(/** @lang GraphQL */ `
                    mutation createRecurringProgramContract(
                        $input: CreateContractInput!
                    ) {
                        createContract(contractInput: $input) {
                            __typename
                            ... on CreateContractResult {
                                recordId
                            }
                            ... on ValidationError {
                                fieldErrors {
                                    message
                                }
                            }
                        }
                    }
                `),
                {
                    input: mutationData
                }
            )

            if (response.createContract.__typename === 'CreateContractResult') {
                // We need the contract ID to query the data in the fragment, so
                // we have to do this as two network operations
                const queryResponse = await query(
                    graphql(`
                        query RecurringProgramCreateContractQuery(
                            $id: ID!
                            $facilityId: ID!
                            $language: Language!
                            $capacityResultFilter: CapacityResultFilter!
                            $bookingStatusFilter: BookingStatusAvailableFilter!
                            $bookingPeriodFilter: BookingPeriodFilter!
                        ) {
                            node(id: $id) {
                                __typename
                                ... on PlanContract {
                                    ...PurchaseRecurringProgramContractFragment
                                }
                            }
                            viewer {
                                cart(facilityId: $facilityId) {
                                    ...ViewerCartFragment
                                }
                            }
                        }
                    `),
                    {
                        id: response.createContract.recordId,
                        facilityId: activeFacility.value.id,
                        language: selectedLanguage.value,
                        capacityResultFilter: {
                            ignoreContractId: response.createContract.recordId
                        },
                        bookingStatusFilter: {
                            customerId: viewer.value?.customerId ?? null
                        },
                        bookingPeriodFilter: {
                            customerId: viewer.value?.customerId ?? null
                        }
                    }
                )

                if (
                    queryResponse.node !== null &&
                    queryResponse.node.__typename === 'PlanContract'
                ) {
                    // Update cart
                    if (queryResponse.viewer !== null) {
                        setCartData(queryResponse.viewer.cart)
                    }
                    return queryResponse.node
                } else {
                    await showAlert(
                        'Heads Up!',
                        'Something went wrong. Please try reloading and clicking on Edit Booking.'
                    )
                }
            }

            if (response.createContract.fieldErrors.length > 0) {
                await showAlert(
                    'Heads Up!',
                    response.createContract.fieldErrors[0].message
                )
            }

            throw new Error()
        }
    })
}
